import React from "react";
import UnifiedTable, {
  TableCellContent,
  TableCellDefinition,
  TableDefinition,
  TableRowDefinition,
} from "./UnifiedTable/UnifiedTable";

export interface TableRegistryEntry {
  type: string;
  title: string;
  source: "generic" | "legacy";
  render: () => React.ReactElement;
}

const textCell = (
  content: TableCellContent,
  options: Omit<TableCellDefinition, "content"> = {}
): TableCellDefinition => ({
  content,
  ...options,
});

const row = (
  cells: Array<TableCellContent | TableCellDefinition>,
  className?: string
): TableRowDefinition => ({
  className,
  cells: cells.map((cell) => {
    if (
      typeof cell === "string" ||
      Array.isArray(cell) ||
      (typeof cell === "object" && cell !== null && "type" in cell)
    ) {
      return textCell(cell as TableCellContent);
    }

    return cell as TableCellDefinition;
  }),
});

const lines = (...items: string[]) => ({
  type: "lines" as const,
  items,
});

const paragraph = (text: string, className?: string) => ({
  type: "paragraph" as const,
  text,
  className,
});

const headingBlock = (text: string, level: 2 | 3 | 4 | 5 | 6 = 4, className?: string) => ({
  type: "heading" as const,
  text,
  level,
  className,
});

const orderedList = (items: string[], className?: string) => ({
  type: "list" as const,
  items,
  ordered: true,
  className,
});

const unorderedList = (items: string[], className?: string) => ({
  type: "list" as const,
  items,
  ordered: false,
  className,
});

const imageBlock = (src: string, alt: string, className?: string) => ({
  type: "image" as const,
  src,
  alt,
  className,
});

const genericEntry = (
  type: string,
  title: string,
  definition: TableDefinition
): TableRegistryEntry => ({
  type,
  title,
  source: "generic",
  render: () => <UnifiedTable definition={definition} />,
});

const genericDefinitions: TableRegistryEntry[] = [
  genericEntry("table", "Pirmosios pagalbos lentelė", {
    className: "table",
    head: [row(["", "Kaulų lūžiai", "Sąnarių išnirimai"])],
    body: [
      row(["Skausmas", "Dažniausiai stiprus ir lokalizuotas", "Stiprus skausmas ties sąnariu"]),
      row([
        "Patinimas",
        "Lūžio vietoje greitai atsiranda patinimas",
        "Greitai atsiranda patinimas, mėlynės",
      ]),
      row([
        "Deformacija",
        "Matoma kaulo deformacija ar neįprasta galūnės padėtis",
        "Ryški sąnario deformacija",
      ]),
      row(["Judrumas", "Galūnė juda ten, kur neturėtų", "Sąnarys tampa nejudrus arba standus"]),
    ],
  }),
  genericEntry("premises-table", "Patalpų apšvietimo lentelė", {
    className: "table",
    head: [row(["Eilės numeris", "Patalpos pavadinimas", "Apšvieta, lx"])],
    body: [
      row([
        "1",
        "Patalpos, kuriose įrengtos vaikų individualios veiklos vietos, stacionarios kompiuterizuotos vietos, pamokų ruošos kambarys (jei yra įrengtas), bendrasis kambarys, miegamasis",
        "300",
      ]),
      row(["2", "Renginių ir kūno kultūros salė", "200"]),
      row(["3", "Judėjimo keliai, laiptinės, koridoriai", "100"]),
      row(["4", "Asmens higienos patalpos", "100"]),
      row(["5", "Virtuvė", "300"]),
    ],
  }),
  genericEntry("interval-table", "Priežiūros dažnumo lentelė", {
    className: "table",
    head: [row(["Reikalavimas", "Vykdymo dažnumas / sąlygos"])],
    body: [
      row([
        "Teritorija turi būti tvarkinga, švari. Žolė – nušienauta. Žiemą takeliai – pabarstyti smėliu, neslidūs.",
        "Nuolat, pagal sezoną",
      ]),
      row([
        "Žaidimų aikštelių smėlis keičiamas ar atnaujinamas. Turi būti be askaridžių, plaukagalvių, toksokarų kiaušinėlių.",
        "Kiekvieną pavasarį ir pagal epidemiologinę situaciją",
      ]),
      row(["Nenaudojamos smėlio dėžės turi būti uždengiamos.", "Kiekvieną kartą po naudojimo"]),
      row(["Šiukšlių konteineriai reguliariai ištuštinami.", "Pagal poreikį"]),
      row(["Patalpos ir įrenginiai valomi drėgnu būdu.", "Kasdien ir pagal poreikį"]),
      row(["Santechnika (unitazai, praustuvės) – tvarkinga ir švari.", "Nuolat"]),
      row(["Valymo ir dezinfekcijos priemonės laikomos vaikams neprieinamoje vietoje.", "Visada"]),
      row([
        "Tualetų ir vonių valymo inventorius ženklinamas ryškia spalva, laikomas atskirai nuo kitų patalpų inventoriaus.",
        "Nuolat",
      ]),
      row(["Indai ir įrankiai plaunami tam skirtomis priemonėmis.", "Po kiekvieno naudojimo"]),
      row(["Langai, šviestuvai valomi.", "Pagal poreikį"]),
      row([
        "Patalynė, rankšluosčiai keičiami.",
        "Pagal poreikį, bet ne rečiau kaip 1 kartą per savaitę",
      ]),
      row([
        "Švari patalynė ir rankšluosčiai laikomi spintose. Nešvari – specialiuose maišuose ar talpyklose.",
        "Nuolat",
      ]),
      row(["Žaislai turi būti švarūs, saugūs, atitikti vaikų amžių.", "Nuolat"]),
      row([
        "Darbuotojai, naudojantys valymo priemones, privalo laikytis gamintojo instrukcijų ir saugos reikalavimų.",
        "Visada",
      ]),
      row([
        "Užkrečiamųjų ligų židinių aplinkos kenksmingumo pašalinimas atliekamas pagal nustatytą tvarką.",
        "Pagal teisės aktą",
      ]),
      row([
        "Patalpose neturi būti graužikų ir nariuotakojų. Atliekama nuolatinė stebėsena ir, nustačius pėdsakų – naikinimas.",
        "Nuolat",
      ]),
      row([
        "Draudžiama atlikti dezinfekciją, dezinsekciją, deratizaciją ar remontą vaikams esant patalpose. Remonto vietos turi būti izoliuotos.",
        "Pagal poreikį, laikantis saugos",
      ]),
    ],
  }),
  genericEntry("temperature-table", "Patalpų temperatūrų lentelė", {
    className: "table",
    head: [row(["Eilės numeris", "Patalpos pavadinimas", "Oro temperatūra, °C"])],
    body: [
      row([
        "1",
        "Patalpos, kuriose įrengtos vaikų individualios veiklos vietos, stacionarios kompiuterizuotos vietos, pamokų ruošos kambarys, bendrasis kambarys, miegamasis",
        "18–22",
      ]),
      row(["2", "Renginių ir kūno kultūros salė", "17–20"]),
      row(["3", "Vestibiulis, drabužinė", "18–20"]),
      row(["4", "Tualetas", "18–22"]),
      row(["5", "Vonios (dušo) patalpa", "20–24"]),
      row(["6", "Laiptinės, koridoriai", "18–20"]),
    ],
  }),
  genericEntry("plant-table", "Nuodingų augalų dalių lentelė", {
    className: "table",
    head: [
      row([
        "Eil. Nr.",
        "Augalas / augalų rūšys",
        "Lotyniškas pavadinimas",
        "Nuodingos augalo dalys",
      ]),
    ],
    body: [
      row([
        "1",
        "Amerikinė fitolaka",
        "Phytolacca americana",
        "Visas augalas, ypač lapai ir uogos",
      ]),
      row(["2", "Baltasis čemerys", "Veratrum album", "Visas augalas, ypač šaknys"]),
      row(["3", "Brugmansijos", "Brugmansia spp.", "Visas augalas"]),
      row(["4", "Darželinis pupmedis", "Laburnum anagyroides", "Visas augalas"]),
      row(["5", "Juodoji drignė", "Hyoscyamus niger", "Visas augalas"]),
      row(["6", "Kukmedžiai", "Taxus spp.", "Visas augalas"]),
      row(["7", "Kurpelės", "Aconitum spp.", "Visas augalas, ypač lapai ir šaknys"]),
      row(["8", "Lobelio čemerys", "Veratrum lobelianum", "Visas augalas, ypač šaknys"]),
      row(["9", "Paprastasis ricinmedis", "Ricinus communis", "Visas augalas, ypač sėklos"]),
      row(["10", "Paprastasis žalčialunkis", "Daphne mezereum", "Visas augalas"]),
      row([
        "11",
        "Paprastoji durnaropė",
        "Datura stramonium",
        "Visas augalas, ypač sėklos ir lapai",
      ]),
      row(["12", "Paprastoji pakalnutė", "Convallaria majalis", "Visas augalas"]),
      row(["13", "Paprastoji rusmenė", "Digitalis purpurea", "Lapai, žiedai, sėklos"]),
      row(["14", "Rudeninis vėlyvis", "Colchicum autumnale", "Visas augalas"]),
      row(["15", "Tabakas", "Nicotiana spp.", "Visas augalas"]),
      row(["16", "Vaistinė šunvyšnė", "Atropa belladona", "Visas augalas, ypač uogos"]),
    ],
  }),
  genericEntry("sanitization-table", "Sanitarinių reikalavimų lentelė", {
    className: "table",
    body: [
      row(["Visi sanitariniai įrenginiai turi būti veikiantys ir techniškai tvarkingi."]),
      row([
        "Tualeto ir prausyklos patalpoje turi būti asmens higienos priemonių: tualetinio popieriaus, muilo, rankų džiovintuvai ar vienkartinių rankšluosčių.",
      ]),
      row([
        "Kiekvienam vaikui turi būti numatyta lova, pagalvė, antklodė, lovos skalbiniai ir ne mažiau kaip 2 rankšluosčiai. Rankšluosčius ir lovos skalbinius į stovyklą vaikai gali atsivežti patys. Apie tai, kad stovykloje nebus aprūpinama minėtomis priemonėmis, turi būti informuojama iš anksto.",
      ]),
      row([
        "Įrenginiai ir baldai turi būti saugūs, tvarkingi. Praustuvai, unitazai turi būti įrengti vaikams pasiekiamame, patogiame naudotis aukštyje.",
      ]),
      row([
        "Jei stovykla aprūpina lovos skalbiniais ir (ar) rankšluosčiais, panaudoti skalbiniai skalbiami pagal sutartį su skalbimo paslaugų teikėjais skalbykloje arba stovykloje įrengtoje atskiroje patalpoje (zonoje), kurioje sumontuota skalbimo ir džiovinimo įranga. Jeigu skalbiniai skalbiami pagal sutartį su skalbimo paslaugų teikėjais, sutartis turi būti laikoma stovykloje, vadovo nustatytoje vietoje.",
      ]),
      row([
        "Visų stovyklos patalpų grindų danga turi būti lygi, neslidi ir lengvai valoma drėgnuoju būdu. Dušų, tualetų, prausyklų ir skalbimo patalpos (zonos) sienos turi būti lygios ir lengvai valomos drėgnuoju būdu.",
      ]),
    ],
  }),
  genericEntry("tool-table", "Stovyklos priežiūros lentelė", {
    className: "table",
    body: [
      row([
        "Stovyklos teritorija",
        "Stovyklos sklypas ir visa teritorija turi būti nuolat prižiūrimi, žolė nušienauta.",
      ]),
      row([
        "Vaikų žaidimų aikštelės",
        "Vaikų žaidimo dėžėse esantis smėlis (jei tokios dėžės įrengtos) turi būti keičiamas arba atnaujinamas ne rečiau kaip kartą per metus.",
      ]),
      row([
        "",
        "Smėlis turi būti apsaugotas nuo užteršimo. Jame negali būti askaridžių, plaukagalvių, toksokarų kiaušinėlių.",
      ]),
      row([
        "Atliekų tvarkymas",
        "Pripildytos buitinių atliekų talpos turi būti reguliariai ištuštinamos.",
      ]),
      row([
        "Patalpų, inventoriaus ir įrangos priežiūra",
        "Stovyklos patalpos, įrenginiai ir inventorius turi būti nuolat švarūs.",
      ]),
      row(["", "Lovos skalbiniai keičiami juos sutepus ir po kiekvienos pamainos."]),
      row([
        "",
        "Dušo, tualetų įrenginiai ir patalpos turi būti reguliariai valomi ir dezinfekuojami.",
      ]),
      row([
        "Valymo ir dezinfekcijos priemonės",
        "Visos valymo ir dezinfekcijos priemonės turi būti naudojamos pagal gamintojo nurodymus ir paskirtį.",
      ]),
      row([
        "",
        "Patalpų ir įrangos dezinfekcijai naudojamos tik teisės aktų nustatyta tvarka įteisintos priemonės.",
      ]),
      row(["", "Šios priemonės laikomos vaikams neprieinamoje vietoje."]),
      row(["Kenkėjų kontrolė", "Patalpose neturi būti graužikų ir kitų buitinių kenkėjų."]),
      row(["", "Kenkėjų kontrolė vykdoma teisės akto nustatyta tvarka."]),
      row([
        "",
        "Privalomasis aplinkos kenksmingumo pašalinimas (dezinfekcija, dezinsekcija, deratizacija) atliekamas teisės akto [4.9] nustatyta tvarka pagal epidemiologines reikmes.",
      ]),
      row([
        "Valymo inventorius",
        "Dušams, tualetams ir kitoms patalpoms valyti naudojamas atskiras, paženklintas inventorius.",
      ]),
      row([
        "",
        "Šis inventorius laikomas švarus, jam skirtose vietose, atskirai nuo kitų patalpų valymo inventoriaus.",
      ]),
      row([
        "Defektų šalinimas",
        "Patalpų naudojimo metu atsiradę defektai (sienų, lubų, grindų, įrenginių ar inventoriaus), galintys turėti įtakos vaikų sveikatai ir saugumui, turi būti šalinami nedelsiant.",
      ]),
      row([
        "Draudimai",
        "Griežtai draudžiama atlikti privalomąjį profilaktinį aplinkos kenksmingumo pašalinimą (dezinfekciją, dezinsekciją, deratizaciją) ar remonto darbus vaikams esant stovyklos patalpose.",
      ]),
    ],
  }),
  genericEntry("temperature-table-premises", "Ikimokyklinių patalpų temperatūrų lentelė", {
    className: "table",
    head: [row(["Eil. Nr.", "Patalpos pavadinimas", "Oro temperatūra, °C"])],
    body: [
      row([
        "1",
        "Grupės priėmimo-nusirengimo, žaidimų patalpa / erdvė, sveikatos kabinetas",
        "20–23",
      ]),
      row(["2", "Grupės miegamasis (jei įrengtas atskirai)", "18–22"]),
      row(["3", "Grupės tualetas-prausykla", "19–23"]),
      row(["4", "Kūno kultūros ir (ar) muzikos salė (jei įrengta)", "18–20"]),
      row(["5", "Patalpa, kurioje įrengtos kompiuterizuotos vietos vaikams", "20–22"]),
      row(["6", "Judėjimo keliai, laiptinės, koridoriai", "18–21"]),
    ],
  }),
  genericEntry("maitenance-table", "Priežiūros punktų lentelė", {
    className: "table",
    body: [
      row(["Laiptų ir laiptų aptvarams reikalavimai."]),
      row(["Įstiklintų durų ženklinimas."]),
      row(["Kiekvienai grupei kokios turi būti įrengtos patalpos."]),
      row(["Vienam vaikui iki 3 metų amžiaus skirta grupės patalpų / erdvių plotas kv."]),
      row(["3 metų ir vyresniam vaikui skirtas plotas kv."]),
      row(["Specialiųjų poreikių turinčiam vaikui plotas kv."]),
      row([
        "Jei vaikai maitinami grupėje, turi būti įrengta patalpa / erdvė su plautuve grupių indams plauti arba automatine indų plovimo mašina ir plautuve rankoms plauti, vieta indams ir stalo įrankiams laikyti.",
      ]),
      row([
        "Bendroje valgymo salėje ar šalia jos turi būti įrengta vieta grupių indams ir stalo įrankiams laikyti, plautuvė grupių indams plauti arba automatinė indų plovimo mašina ir plautuvė rankoms plauti.",
      ]),
    ],
  }),
  genericEntry("animal-table", "Gyvūnų laikymo lentelė", {
    className: "table",
    body: [
      row(["Laikomi tik atskiroje patalpoje."]),
      row([
        "Saugiai įrengti akvariumai su žuvytėmis gali būti laikomi ir grupių patalpose / erdvėse, jei nėra alergiškų vaikų.",
      ]),
    ],
  }),
  genericEntry("poisonous-plants-table", "Nuodingų augalų rūšių lentelė", {
    className: "table",
    head: [row(["Eil. Nr.", "Augalas / augalų rūšys", "Lotyniškas pavadinimas"])],
    body: [
      row(["1", "Amerikinė fitolaka", "Phytolacca americana"]),
      row(["2", "Barščiai", "Heracleum spp."]),
      row(["3", "Pieriai", "Pieris spp."]),
      row(["4", "Brugmansijos", "Brugmansia spp."]),
      row(["5", "Čemeriai", "Veratrum spp."]),
      row(["6", "Darželinis pupmedis", "Laburnum anagyroides"]),
      row(["7", "Drignės", "Hyoscyamus spp."]),
      row(["8", "Durnaropės", "Datura spp."]),
      row(["9", "Eleborai", "Helleborus spp."]),
      row(["10", "Juodžolės", "Actaea spp."]),
      row(["11", "Kukmedžiai", "Taxus spp."]),
      row(["12", "Kurpelės", "Aconitum spp."]),
      row(["13", "Ligustrai", "Ligustrum spp."]),
      row(["14", "Oleandrai", "Nerium spp."]),
      row(["15", "Ožekšniai", "Euonymus spp."]),
      row(["16", "Paprastasis ricinmedis", "Ricinus communis"]),
      row(["17", "Paprastasis žalčialunkis", "Daphne mezereum"]),
      row(["18", "Paprastoji pakalnutė", "Convallaria majalis"]),
      row(["19", "Paukštpienės", "Ornithogalum spp."]),
      row(["20", "Pentiniai", "Delphinium spp."]),
      row(["21", "Baltažiedė robinija", "Robinia pseudoacacia"]),
      row(["22", "Rododendrai", "Rhododendron spp."]),
      row(["23", "Rudeninis vėlyvis", "Colchicum autumnale"]),
      row(["24", "Rusmenės", "Digitalis spp."]),
      row(["25", "Scylės", "Scilla spp."]),
      row(["26", "Tabakas", "Nicotiana spp."]),
      row(["27", "Tinūtrai", "Adenium spp."]),
      row(["28", "Vaistinė skopolija", "Scopolia carniolica"]),
      row(["29", "Vaistinė šunvyšnė", "Atropa belladona"]),
    ],
  }),
  genericEntry("poisonous-plants-table-en", "List Of Poisonous Plants", {
    className: "table",
    head: [row(["No.", "Plant / plant species", "Latin name"])],
    body: [
      row(["1", "American pokeweed", "Phytolacca americana"]),
      row(["2", "Hogweeds", "Heracleum spp."]),
      row(["3", "Pieris", "Pieris spp."]),
      row(["4", "Angel's trumpets", "Brugmansia spp."]),
      row(["5", "False hellebores", "Veratrum spp."]),
      row(["6", "Golden chain tree", "Laburnum anagyroides"]),
      row(["7", "Henbanes", "Hyoscyamus spp."]),
      row(["8", "Daturas (Jimsonweeds)", "Datura spp."]),
      row(["9", "Hellebores", "Helleborus spp."]),
      row(["10", "Baneberries", "Actaea spp."]),
      row(["11", "Yews", "Taxus spp."]),
      row(["12", "Monkshoods (Aconites)", "Aconitum spp."]),
      row(["13", "Privets", "Ligustrum spp."]),
      row(["14", "Oleanders", "Nerium spp."]),
      row(["15", "Spindle trees", "Euonymus spp."]),
      row(["16", "Castor bean plant", "Ricinus communis"]),
      row(["17", "February daphne (Mezereon)", "Daphne mezereum"]),
      row(["18", "Lily of the valley", "Convallaria majalis"]),
      row(["19", "Star-of-Bethlehem plants", "Ornithogalum spp."]),
      row(["20", "Larkspurs", "Delphinium spp."]),
      row(["21", "Black locust", "Robinia pseudoacacia"]),
      row(["22", "Rhododendrons", "Rhododendron spp."]),
      row(["23", "Autumn crocus", "Colchicum autumnale"]),
      row(["24", "Foxgloves", "Digitalis spp."]),
      row(["25", "Squills", "Scilla spp."]),
      row(["26", "Tobacco plants", "Nicotiana spp."]),
      row(["27", "Desert roses", "Adenium spp."]),
      row(["28", "Scopolia", "Scopolia carniolica"]),
      row(["29", "Deadly nightshade", "Atropa belladonna"]),
    ],
  }),
  genericEntry("luminosity-table", "Apšvietos normų lentelė", {
    className: "table",
    head: [
      row([
        "Eil. Nr.",
        "Patalpos pavadinimas",
        "Apšvieta, lx",
        "Paviršius, kuriam taikoma apšvieta",
      ]),
    ],
    body: [
      row(["1", "Mokymo klasė, mokymo kabinetas", "300", "stalo horizontalus paviršius"]),
      row(["", "", "500", "lentos vertikalus paviršius"]),
      row([
        "2",
        "Gamtos mokslų kabinetas, konstrukcinių medžiagų dirbtuvės, elektronikos mokymo kabinetas, mokomoji virtuvė, tekstilės mokymo kabinetas, skaitykla",
        "500",
        "stalo horizontalus paviršius",
      ]),
      row([
        "3",
        "Informacinių technologijų mokymo kabinetas",
        "300",
        "stalo horizontalus paviršius",
      ]),
      row(["", "", "100", "monitoriaus vertikalus paviršius"]),
      row(["4", "Sporto salė", "300", "ant grindų paviršiaus"]),
      row(["5", "Aktų salė", "200", "ant grindų paviršiaus"]),
      row([
        "6",
        "Persirengimo kambarys, drabužinė, tualetas, dušas",
        "200",
        "ant grindų paviršiaus",
      ]),
      row(["7", "Laiptinė", "150", "ant grindų paviršiaus"]),
      row(["8", "Koridorius", "100", "ant grindų paviršiaus"]),
    ],
  }),
  genericEntry("luminosity-table-en", "Minimum Limit Values For Artificial Lighting", {
    className: "table",
    head: [row(["No.", "Name of premises", "Illuminance, lx", "Surface to which the illuminance applies"])],
    body: [
      row(["1", "Classroom, subject classroom", "300", "Horizontal desk surface"]),
      row(["", "", "500", "Vertical surface of the board"]),
      row([
        "2",
        "Natural sciences classroom, construction materials workshop, electronics classroom, teaching kitchen, textile classroom, reading room",
        "500",
        "Horizontal desk surface",
      ]),
      row(["3", "Information technology classroom", "300", "Horizontal desk surface"]),
      row(["", "", "100", "Vertical surface of the monitor"]),
      row(["4", "Sports hall", "300", "Floor surface"]),
      row(["5", "Assembly hall", "200", "Floor surface"]),
      row(["6", "Changing room, cloakroom, toilet, shower room", "200", "Floor surface"]),
      row(["7", "Staircase", "150", "Floor surface"]),
      row(["8", "Corridor", "100", "Floor surface"]),
    ],
  }),
  genericEntry("legend-table", "Sąvokų lentelė", {
    className: "table",
    head: [row(["Sąvoka", "Apibrėžimas"])],
    body: [
      row([
        "A kategorijos grožio paslauga",
        "Grožio paslauga, kurią teikiant pažeidžiama paslaugos gavėjo oda arba gleivinė ir tam skirtas instrumentas gali būti užteršiamas krauju arba kitais kūno skysčiais. Prie šių paslaugų priskiriamos tatuiravimo, ilgalaikio makiažo, papuošalų vėrimo, plaukų skutimo, manikiūro ir pedikiūro (kai karpomos nagų odelės) ir kitos invazinės grožio paslaugos.",
      ]),
      row([
        "B kategorijos grožio paslauga",
        "Grožio paslauga, kurią teikiant nepažeidžiama paslaugos gavėjo oda arba gleivinė. Prie šių paslaugų priskiriamos veido ir kūno, plaukų priežiūros, manikiūro ir pedikiūro (kai nekarpomos nagų odelės), plaukų šalinimo vašku ar pan., dekoratyvinės kosmetikos ir kitos neinvazinės paslaugos.",
      ]),
      row([
        "Dekoratyvinės kosmetikos paslauga",
        "Paslauga, kurią teikiant ant paslaugos gavėjo epidermio, plaukų, nagų, lūpų užtepama, užpurškiama, užklijuojama ar pan. tam tikro dekoratyvinės kosmetikos gaminio.",
      ]),
      row([
        "Dezinfekcija",
        "Daugelio arba visų mikroorganizmų, išskyrus bakterijų sporas, sunaikinimas aplinkoje fizinėmis ir cheminėmis priemonėmis.",
      ]),
      row([
        "Grožio paslauga",
        "Paslauga, kurios tikslas – įvairiomis priemonėmis ir būdais išlaikyti ar pagerinti žmogaus plaukų, veido, kūno, nagų išvaizdą, apsaugoti ir (arba) palaikyti gerą plaukų, odos, nagų būklę, paryškinti patraukliuosius bruožus ir kuri teikiama naudojant priemones ar prietaisus, kurie pagal gamintojo nurodytas instrukcijas skirti buitiniam ir (arba) grožio paslaugų teikėjo profesionaliajam naudojimui.",
      ]),
      row([
        "Instrumento valymas",
        "Pirminis teršalų nušluostymas nuo instrumento, jo mirkymas ir plovimas, skalavimas vandeniu.",
      ]),
      row([
        "Ilgalaikio makiažo paslauga",
        "Specialių dažų ar pigmentų įterpimas į odą veido srityje, paryškinant ar pakoreguojant antakių, akių, lūpų kontūrus.",
      ]),
      row([
        "Nagų priežiūros paslauga",
        "Manikiūro, pedikiūro, dirbtinių nagų priauginimo ar kita su nagų priežiūra susijusi grožio paslauga.",
      ]),
      row(["Papuošalų vėrimo paslauga", "Papuošalų vėrimas į kurią nors kūno vietą."]),
      row([
        "Plaukų priežiūros paslauga",
        "Plaukų plovimo, plaukų kaukių dėjimo, kirpimo, skutimo, šukuosenos formavimo, dažymo, ilgalaikio plaukų sušukavimo, plaukų priauginimo, tiesinimo ar kita su plaukų priežiūra susijusi grožio paslauga.",
      ]),
      row([
        "Sanitizatorius",
        "Įrenginys, kuriame išvalyti instrumentai dezinfekuojami ultravioletiniais spinduliais.",
      ]),
      row([
        "Sterilizacija",
        "Mikroorganizmų ir jų sporų sunaikinimas fizinėmis ir cheminėmis priemonėmis.",
      ]),
      row([
        "Sterilus instrumentas",
        "Instrumentas, ant kurio nėra gyvybingų mikroorganizmų ir jų sporų.",
      ]),
      row([
        "Tatuiravimo paslauga",
        "Ilgalaikio ženklo ar piešinio ant odos atlikimas adata įterpiant specialių dažų ar pigmentų.",
      ]),
      row([
        "Veido ir kūno priežiūros paslauga",
        "Odos valymo, maitinimo, drėkinimo, kosmetinių kaukių dėjimo, plaukų šalinimo, masažo (išskyrus gydomojo masažo) ar kita su veido ir kūno priežiūra susijusi grožio paslauga.",
      ]),
    ],
  }),
  genericEntry("microbial-table", "Mikrobinio užterštumo lentelė", {
    className: "table",
    head: [
      row([
        "Eil. Nr.",
        "Mikrooganizmų rūšis",
        textCell("Mikrobinio užterštumo ribinės vertės (1 g arba 1 ml)", {
          colSpan: 3,
        }),
      ]),
      row(["", "", "I kategorijos kosmetikos gaminiai", "II kategorijos kosmetikos gaminiai"]),
    ],
    body: [
      row([
        "1.",
        "Aerobinių mezofilinių bakterijų ir mielių bei pelėsių, užaugusių standžioje terpėje, skaičius 1 g arba 1 ml (toliau – Bendras aerobinių mezofilinių bakterijų skaičius)",
        "≤ 1 x 10²*",
        "≤ 1 x 10³*",
      ]),
      row(["2.", "Auksinis stafilokokas (Staphylococcus aureus)", "Neturi būti", "Neturi būti"]),
      row(["3.", "Žaliamėlė pseudomona (Pseudomonas aeruginosa)", "Neturi būti", "Neturi būti"]),
      row(["4.", "Baltasis balkšvagrybis (Candida albicans)", "Neturi būti", "Neturi būti"]),
      row(["5.", "Žarninė lazdelė (Escherichia coli)", "Neturi būti", "Neturi būti"]),
    ],
  }),
  genericEntry("microbial-size-table", "Kosmetikos rodiklių lentelė", {
    className: "table",
    head: [
      row([
        "Eil. Nr.",
        "Kosmetikos gaminio fizikiniai ir cheminiai rodikliai",
        "Ribiniai dydžiai",
        "Kosmetikos gaminių pavyzdžiai",
      ]),
    ],
    body: [
      row(["1.", "pH", "≤ 3,0", "Odos šveitikliai (glikolio rūgšties pagrindu)"]),
      row(["2.", "pH", "≥ 10,0", "Plaukų tiesinimo priemonės"]),
      row(["3.", "Bevandenis kosmetikos gaminys", "", "Kūno aliejai, pieštukai"]),
      row([
        "4.",
        "Kosmetikos gaminio galutinės formulės vandens aktyvumas (aw)",
        "≤ 0,75",
        "Lūpų balzamas, lūpų dažai, skaistalai, gabaliniai tualetiniai muilai ir gabaliniai kietieji prausikliai",
      ]),
      row([
        "5.",
        textCell("Kosmetikos gaminio sudėtyje esančių cheminių medžiagų ar jų junginių kiekis:", {
          colSpan: 3,
        }),
      ]),
      row(["5.1.", "etilo ir kitas alkoholis", "≥ 20 %", "Plaukų purškikliai, tonikai, kvepalai"]),
      row([
        "5.2.",
        "organiniai tirpikliai: etilacetatas (Ethyl acetate), butilacetatas (Butyl acetate)",
        "10,0 %",
        "Gaminiai tirpiklių pagrindu: pvz., nagų emaliai",
      ]),
      row(["5.3.", textCell("Šarminiai junginiai:", { colSpan: 3 })]),
      row([
        "5.3.1.",
        "amoniakas (Ammonia)",
        "≥ 0,5 %",
        "Oksiduojantieji gaminiai, plaukų dažai, ilgalaikio formavimo gaminiai",
      ]),
      row([
        "5.3.2.",
        "monoetanolaminas (Monoethanolamine)",
        "≥ 1 %",
        "Oksiduojantieji gaminiai, plaukų dažai, ilgalaikio formavimo gaminiai",
      ]),
      row([
        "5.4.",
        "aliuminio chlorhidratas (Aluminium chlorohydrate)",
        "≥ 25 %",
        "Antiperspirantai",
      ]),
      row([
        "5.5.",
        "vandenilio peroksidas (Hydrogen peroxyde)",
        "≥ 3 %",
        "Plaukų šviesinimo gaminiai",
      ]),
      row([
        "6.",
        "Kosmetikos gaminio gamybos sąlygos: kosmetikos gaminio temperatūra gamybos proceso metu",
        "≥ 65,0 °C",
        "Lūpų balzamas, lūpų dažai, skaistalai",
      ]),
    ],
  }),
  genericEntry("meanings-table", "Sąvokų paaiškinimų lentelė", {
    className: "table",
    head: [row(["Sąvoka", "Apibrėžimas"])],
    body: [
      row([
        'Maistas (arba „maisto produktas")',
        "Tai medžiaga arba produktas, perdirbtas, perdirbtas iš dalies arba neperdirbtas, kurį žmogus nurys arba pagrįstai tikimasi, kad nurys.",
      ]),
      row([
        "Maisto sauga",
        "Tam tikrų sąlygų sudarymas maisto gaminimo įmonėse, kad būtų užtikrinta maisto kokybė ir apsaugotas maistas nuo užteršimo ir apnuodijimo.",
      ]),
      row([
        "Maisto higiena",
        "Maisto higiena apima visas sritis, susijusias su maisto saugumo priemonėmis, saugos reikalavimais ruošiant, laikant ir apdorojant įvairius maisto produktus ir žaliavas.",
      ]),
      row([
        "Maisto tarša",
        "Tai medžiagos, kurios sugadina ar užteršia maistą, nuo kurių maistas tampa netinkamu vartoti. Maisto tarša visuomet susijusi su neigiamomis pasekmėmis, neigiamomis emocijomis, ūmių apsinuodijimų ar ūmių infekcinių žarnyno susirgimų protrūkiais.",
      ]),
      row([
        "Kryžminė tarša",
        "Procesas, kurio metu maistas yra užteršiamas bakterijomis ir įvairiomis kenksmingomis medžiagomis.",
      ]),
    ],
  }),
  genericEntry("meanings-table-en", "Food safety terms table", {
    className: "table",
    head: [row(["Term", "Definition"])],
    body: [
      row([
        "Food (or “food product”)",
        "A substance or product, processed, partially processed, or unprocessed, that a person consumes or is reasonably expected to consume.",
      ]),
      row([
        "Food safety",
        "The establishment of certain conditions in food production companies to ensure food quality and protect food from contamination and poisoning.",
      ]),
      row([
        "Food hygiene",
        "Food hygiene encompasses all areas related to food safety measures and safety requirements during the preparation, storage, and processing of various food products and raw materials.",
      ]),
      row([
        "Food contamination",
        "Substances that spoil or contaminate food, making it unsuitable for consumption. Food contamination is always associated with negative consequences, negative emotions, outbreaks of acute poisonings or acute infectious intestinal diseases.",
      ]),
      row([
        "Cross-contamination",
        "The process by which food becomes contaminated with bacteria and various harmful substances.",
      ]),
    ],
  }),
  genericEntry("lx-table", "Apšvietos ribų lentelė", {
    className: "table",
    head: [row(["Nr.", "Patalpos pavadinimas", "Apšvieta, lx"])],
    body: [
      row(["1.", "Gyvenamieji miegamieji, bendrieji kambariai, valgyklos", "150"]),
      row(["2.", "Ugdymo patalpos", "300"]),
      row(["3.", "Judėjimo keliai, laiptinės, koridoriai, laukiamieji", "100"]),
      row(["4.", "Asmens higienos patalpos", "100"]),
      row(["5.", "Virtuvės-valgomieji", "300"]),
    ],
  }),
  genericEntry("micro-table", "Mikroklimato parametrų lentelė", {
    className: "table",
    head: [
      row([
        textCell("Mikroklimato parametrai", { rowSpan: 2 }),
        textCell("Ribinės vertės", { colSpan: 2 }),
      ]),
      row(["Šaltuoju metų laikotarpiu", "Šiltuoju metų laikotarpiu"]),
    ],
    body: [
      row(["Oro temperatūra, °C", "18-22", "18-28"]),
      row([
        "Temperatūrų skirtumas 0,1 m ir 1,1 m aukštyje nuo grindų, ne daugiau kaip °C",
        "3",
        "3",
      ]),
      row(["Santykinė oro drėgmė, %", "35-60", "35-65"]),
      row(["Oro judėjimo greitis, m/s", "0,05-0,15", "0,15-0,25"]),
    ],
  }),
  genericEntry("precaution-table", "Saugos priemonių lentelė", {
    className: "table",
    head: [
      row([textCell("INFORMACIJA APIE SAUGOS PRIEMONES IR JŲ NAUDOJIMĄ", { colSpan: 2 })]),
      row(["Apsauga akims", "Apsauga kūnui"]),
    ],
    body: [
      row(["Apsauginis stiklas", "Prijuostė"]),
      row(["Apsauginiai akiniai", "Pirštinės"]),
      row(["Apsauginis skydas", "Avalynė"]),
      row(["", "Chalatas"]),
      row(["", "Spec. apranga"]),
    ],
  }),
  genericEntry("antiseptic-table", "Rankų antiseptikos lentelė", {
    className: "table",
    body: [
      row([
        "1",
        "Ant sausų švarių rankų (į saują) alkūne, dilbiu ar išorine plaštakos puse iš dozatoriaus išspaudžiama reikalinga alkoholinio rankų antiseptiko dozė ir išskirstoma ant abiejų rankų plaštakų.",
      ]),
      row([
        "2",
        "Alkoholiniu antiseptiku trinamaisiais ir sukamaisiais judesiais kruopščiai trinami riešai, tarpupirščiai, pirštų galiukai, nagai, nykščiai.",
      ]),
      row([
        "3",
        "Plaštakos visą trynimo laiką turi būti drėgnos, jei reikia, užpilama rankų antiseptiko papildomai.",
      ]),
      row([
        "4",
        "Rankos (jei dirbote be pirštinių), suteptos krauju ir (ar) kitais kūno skysčiais, ekskretais arba lietus jomis daiktus, paviršius, užterštus krauju ir (ar) kitais kūno skysčiais, ekskretais, plaunamos ir atliekama higieninė rankų antiseptika, o nesant galimybės –atliekama tik higieninė rankų antiseptika.",
      ]),
    ],
  }),
  genericEntry("chirurgical-hand-table", "Chirurginės rankų antiseptikos lentelė", {
    className: "table",
    body: [
      row([
        "1",
        "Rankos plaunamos ne trumpiau kaip 1 min. Prieš pirmą darbo dienos operaciją būtina rankas plauti iki alkūnių.",
      ]),
      row([
        "2",
        "Nuplautos rankos sausinamos (netrinant) vienkartinėmis švariomis servetėlėmis; kiekviena ranka sausinama atskira servetėle. Rankos pradedamos sausinti nuo pirštų ir baigiamos alkūnių sausinimu, neliečiant ta pačia servetėle anksčiau nusausintos odos vietų.",
      ]),
      row([
        "3",
        "Alkūne, dilbiu ar išorine plaštakos puse paspaudžiama antiseptiko dozatoriaus rankenėlė, sudrėkinama ir įtrinama rankų plaštakų, riešų, dilbių oda alkoholiniu antiseptiniu tirpalu.",
      ]),
      row([
        "4",
        "Trinamaisiais, sukamaisiais judesiais rankų oda trinama 2 kartus šia tvarka: plaštakos, riešai, dilbiai ir alkūnės, dar kartą dilbiai, riešai, plaštakos. Ypač kruopščiai įtrinami riešai, tarpupirščiai, pirštų galiukai, nagai, nykščiai.",
      ]),
      row([
        "5",
        "Visą antiseptikos laiką trinama oda turi būti drėgna, jei reikia, papildomai užpilama antiseptiko tirpalo. Paskutinė antiseptiko dozė trinama, kol oda išdžiūsta.",
      ]),
      row(["6", "Užsimaunamos vienkartinės sterilios medicininės pirštinės."]),
    ],
  }),
  genericEntry("cleaning-table", "Valymo priemonių lentelė", {
    className: "table",
    head: [
      row([
        "Valomas objektas",
        "Sveikatos priežiūros sektorius",
        "Ne sveikatos priežiūros sektorius (esant įtarimui ar patvirtinimui)",
        "Bendros paskirties patalpos",
      ]),
    ],
    body: [
      row([
        "Paviršiai",
        "Neutralus valiklis + biocidas / 0,05 % NaClO / 70 % etanolis",
        "Tas pats",
        "Neutralus valiklis",
      ]),
      row([
        "Tualetai",
        "Biocidas / 0,1 % NaClO",
        "Tas pats",
        "Biocidas / 0,1 % NaClO (pasirinktinai)",
      ]),
      row(["Tekstilė", "90°C skalbimas arba + baliklis", "Tas pats", "Netaikoma"]),
      row([
        "Inventorius",
        "Vienkartiniai / dezinfekuoti daugkartiniai",
        "Tas pats",
        "Valomi po kiekvieno naudojimo",
      ]),
      row([
        "Valytojo AAP",
        "Kaukė, pirštinės, chalatas, respiratorius (jei reikia)",
        "Kaukė, darbiniai drabužiai, pirštinės",
        "Darbiniai drabužiai, pirštinės",
      ]),
      row([
        "Atliekos",
        "B kategorijos infekuotos atliekos (UN3291)",
        "Atskiras maišas į bendrąsias atliekas",
        "Nerūšiuojamos atliekos",
      ]),
    ],
  }),
  genericEntry("room-table", "Patalpų temperatūrų ribų lentelė", {
    className: "table",
    head: [row(["Patalpos", "Temperatūrų ribinės vertės, °C"])],
    body: [
      row(["Laiptinės, koridoriai, holai, vestibiuliai", "14-16"]),
      row(["Bendros virtuvės", "18-22"]),
      row(["Tualetai, prausyklos, dušai, vonios kambariai", "20-23"]),
      row(["Rūsiai ir sandėliai", "4-8"]),
      row(["Darbo ir poilsio kambariai", "18-22"]),
      row(["Skalbyklos", "18-22"]),
      row(["Džiovyklos", "20-23"]),
    ],
  }),
  genericEntry("noise-table", "Triukšmo lygių lentelė", {
    className: "table",
    head: [
      row([
        "Objekto pavadinimas",
        "Paros laikas*",
        "Ekvivalentinis garso slėgio lygis (LAeqT), dBA",
        "Maksimalus garso slėgio lygis (LAFmax), dBA",
      ]),
    ],
    body: [
      row([
        "Gyvenamųjų pastatų gyvenamosios patalpos, visuomeninės paskirties pastatų miegamieji kambariai, stacionarinių asmens sveikatos priežiūros įstaigų palatos",
        lines("diena", "vakaras", "naktis"),
        lines("45", "40", "35"),
        lines("55", "50", "45"),
      ]),
      row([
        "Gyvenamųjų pastatų ir visuomeninės paskirties pastatų (išskyrus maitinimo ir kultūros paskirties pastatus) aplinkoje, veikiamoje transporto sukeliamo triukšmo",
        lines("diena", "vakaras", "naktis"),
        lines("65", "60", "55"),
        lines("70", "65", "60"),
      ]),
      row([
        "Gyvenamųjų pastatų ir visuomeninės paskirties pastatų (išskyrus maitinimo ir kultūros paskirties pastatus) aplinkoje, išskyrus transporto sukeliamą triukšmą",
        lines("diena", "vakaras", "naktis"),
        lines("55", "50", "45"),
        lines("60", "55", "50"),
      ]),
    ],
  }),
  genericEntry("lighting-table", "Apšvietos lentelė", {
    className: "table",
    head: [
      row([
        "Eil. Nr.",
        "Patalpos pavadinimas",
        "Apšvieta, lx",
        "Paviršius, kuriam taikoma apšvieta",
      ]),
    ],
    body: [
      row([
        "1",
        "Grupės žaidimų patalpa / erdvė, patalpa, kurioje įrengtos kompiuterizuotos vietos vaikams",
        "300",
        "horizontalus paviršius 0,5m aukštyje nuo grindų",
      ]),
      row([
        "2",
        "Kūno kultūros ir (ar) muzikos salė (jei įrengta)",
        "200",
        "horizontalus paviršius 0,5m aukštyje nuo grindų",
      ]),
      row([
        "3",
        "Grupės priėmimo-nusirengimo patalpa / erdvė",
        "200",
        "horizontalus paviršius 0,8m aukštyje nuo grindų",
      ]),
      row([
        "4",
        "Grupės miegamasis (jei įrengtas atskirai)",
        "75",
        "horizontalus paviršius 0,5m aukštyje nuo grindų",
      ]),
      row([
        "5",
        "Grupės tualetas-prausykla, judėjimo keliai, laiptinės, koridoriai",
        "100",
        "horizontalus paviršius 0,5m aukštyje nuo grindų",
      ]),
    ],
  }),
  genericEntry("requirements-table", "Darbo vietos reikalavimų lentelė", {
    className: "table",
    head: [
      row(["GERAI ĮRENGTA DARBO KOMPIUTERIU VIETA", "NETEISINGAI DIRBAMA KOMPIUTERIU, JEIGU:"]),
    ],
    body: [
      row([
        [
          paragraph(
            "Dirbti galima tik su kokybiškais, techniškai tvarkingais ir sertifikuotais kompiuteriais."
          ),
          paragraph(
            "Darbo su kompiuteriu vieta turi būti pakankamai apšviesta. Neturėtų būti pašalinių blyksnių."
          ),
          paragraph(
            "Stalo aukštis turi būti toks, kad dirbant rankos nebūtų pakeltos aukštyn arba pernelyg nuleistos žemyn."
          ),
          paragraph("Darbo stalo paviršius turi būti tvirtas ir stabilus."),
          paragraph(
            "Jei turite galimybę, geriausia darbo vietoje turėti reguliuojamo aukščio kėdę. Tuomet galima teisingame aukštyje išlaikyti regėjimo kampą."
          ),
          paragraph("Patalpos, kuriose dirbama kompiuteriu, turi būti nuolatos vėdinamos."),
        ],
        [
          paragraph(
            "Įtemptai dirbama ar žaidžiama kompiuteriu 1 – 2 valandas ir daugiau be pertraukos."
          ),
          paragraph(
            "Dirbant kompiuteriu sėdima persikreipus, o pėdos nėra iki galo atremtos į grindis."
          ),
          paragraph("Atstumas tarp dirbančiojo akių ir monitoriaus mažesnis nei 40 cm."),
          paragraph("Patalpoje, kurioje dirbama kompiuteriu yra prieblanda, arba visiškai tamsu."),
        ],
      ]),
    ],
  }),
  genericEntry("feeding-table", "Vaikų maitinimo lentelė", {
    className: "table",
    head: [row(["Vaikų iki 1 m. amžiaus maitinimo reikalavimai"])],
    body: [
      row([
        "0‒6 mėn. amžiaus vaikams tiekiamas motinos pienas, jo nesant, pradinio maitinimo kūdikių mišinys.",
      ]),
      row([
        "6‒7 mėn. amžiaus vaikams turi būti tiekiamas motinos pienas, jo nesant, tolesnio maitinimo kūdikių mišinys; dviejų‒trijų daržovių (bulvių, cukinijų, moliūgų, morkų, ropių, burokėlių, brokolių, žiedinių kopūstų ir kopūstų) trintos košės, pagardintos šalto spaudimo aliejumi; smulkintų vienos rūšies kruopų (grikių, ryžių, avižų, miežių ar kukurūzų) košės (be pieno). Prie tiršto maisto tiekiamas geriamasis vanduo.",
      ]),
      row([
        "7‒8 mėn. amžiaus vaikams turi būti tiekiamas motinos pienas, jo nesant, mišinys; košės, pagardintos sviestu ar šalto spaudimo aliejumi; mėsa (paukštiena, veršiena, triušiena ar kiauliena) su koše. Atskiro maitinimo metu tiekiami trinti vaisiai (obuoliai, slyvos, persikai) ar uogos. Prie tiršto maisto tiekiamas geriamasis vanduo.",
      ]),
      row([
        "8‒9 mėn. amžiaus vaikams turi būti tiekiamas motinos pienas, jo nesant, mišinys; dviejų‒trijų daržovių košės; vienos ar kelių rūšių kruopų košės. Košės gardinamos prieskoninėmis žolelėmis, sviestu ar šalto spaudimo aliejumi. Tiekiama mėsa, smulkintas kiaušinio trynys. Atskiro maitinimo metu tiekiami trinti ar smulkinti vaisiai ar uogos. Prie tiršto maisto tiekiamas geriamasis vanduo.",
      ]),
      row([
        "9‒11 mėn. amžiaus vaikų valgiaraščiai papildomi smulkintais melionais, bananais, pomidorais, kefyru ir natūraliu jogurtu.",
      ]),
      row([
        "11‒12 mėn. amžiaus vaikų valgiaraščiai papildomi smulkintais kiviais ir varškės patiekalais, pagamintais tausojančiu būdu.",
      ]),
    ],
  }),
  genericEntry("hand-table", "Rankų plovimo lentelė", {
    className: "table",
    head: [row(["RANKŲ PLOVIMO INSTRUKCIJA"])],
    body: [
      row([
        orderedList([
          "Rankos apnuoginamos iki dilbių vidurio. Chirurginio rankų paruošimo metu rankos apnuoginamos virš alkūnių.",
          "Rankos sudrėkinamos vėsiu vandeniu.",
          "Alkūne, dilbiu ar išorine plaštakos puse paspaudžiama skysto muilo dozatoriaus rankenėlė ir užpilama apie 3 ml skysto muilo (neliečiant dozatoriaus ištekėjimo angos).",
          "Kruopščiai 10–15 sek. rankos muiluojamos trinamaisiais ir sukamaisiais judesiais, ypač riešai, tarpupirščiai, pirštų galiukai ir nagai, nykščiai.",
          "Muiluotos rankos nuplaunamos po vandens srove. Vanduo turi tekėti nuo riešo pirštų link, chirurginio rankų paruošimo metu – nuo pirštų alkūnių link.",
          "Rankos nusausinamos vienkartiniu rankšluosčiu ar servetėle. Jei vandens čiaupai užsukami ranka, juos reikia užsukti su panaudotu vienkartiniu rankšluosčiu ar servetėle.",
          "Panaudotas vienkartinis rankšluostis ar servetėlė išmetami į šiukšliadėžę.",
        ]),
      ]),
    ],
  }),
  genericEntry("illness-table", "Užkrečiamųjų ligų lentelė", {
    className: "table",
    body: [
      row([textCell("UŽKREČIAMŲJŲ LIGŲ PERDAVIMAS", { colSpan: 2, as: "th" })]),
      row([
        textCell("Infekcijos šaltinis ← → Imlus organizmas ← → Perdavimo kelias", { colSpan: 2 }),
      ]),
      row([
        textCell("Pašalinus nors vieną iš šių veiksnių epidemija ir infekcinės ligos nebeplinta.", {
          colSpan: 2,
        }),
      ]),
      row([textCell("UŽKREČIAMŲJŲ LIGŲ PROFILAKTIKA", { colSpan: 2, as: "th" })]),
      row([
        textCell(
          [
            paragraph(
              "Profilaktikos priemonės nukreiptos į infekcinio susirgimo plitimo grandines:"
            ),
            headingBlock("Sergantis organizmas ir sukėlėjas"),
            unorderedList([
              "Ankstyvas sergančiųjų ir nešiotojų išaiškinimas ir gydymas",
              "Sergančiųjų ir nešiotojų izoliacija",
              "Kontaktų išaiškinimas",
              "Aplinkos dezinsekcija, deratizacija",
            ]),
            headingBlock("Plitimo kelias"),
            unorderedList([
              "Asmens higienos priemonės",
              "Maisto ruošimo taisyklių paisymas, maisto produktų tikrinimas",
              "Masinio susibūrimo vietų vengimas epidemijos metu",
              "Aplinkos dezinfekcija",
              "Patalpų vėdinimas",
              "Kraujo donorų ir kraujo produktų tikrinimas",
            ]),
            headingBlock("Imlus organizmas"),
            unorderedList([
              "Kontakto vengimas",
              "Imuniteto stiprinimas",
              "Individualių apsaugos priemonių naudojimas",
              "Imunoprofilaktika",
            ]),
            headingBlock("Ką galiu padaryti pats, norėdamas išvengti infekcinių susirgimų"),
            unorderedList([
              "Vengti kontaktų",
              "Plautis rankas",
              "Gerti tik švarų ar virintą vandenį",
              "Vartoti tik patikimus ir kokybiškus maisto produktus",
              "Stiprinti imunitetą",
              "Susirgus likti namie",
              "Pasiskiepyti",
            ]),
          ],
          { colSpan: 2 }
        ),
      ]),
    ],
  }),
  genericEntry("pictogram-table", "Piktogramų lentelė", {
    className: "table",
    head: [row(["Piktogramos pavadinimas", "Piktograma", "Reikšmė"])],
    body: [
      row([
        "Sprogstanti bomba",
        imageBlock("/assets/hygieneh15/2/0.jpg", "Sprogstanti bomba"),
        lines(
          "Nestabilios sprogstamosios medžiagos;",
          "Savaime reaguojančios medžiagos ir mišiniai;",
          "Organiniai peroksidai."
        ),
      ]),
      row([
        "Liepsnojantis lankas",
        imageBlock("/assets/hygieneh15/2/1.jpg", "Liepsnojantis lankas"),
        lines(
          "Oksiduojančios dujos;",
          "Oksiduotieji skysčiai;",
          "Oksiduojančiosios kietosios medžiagos."
        ),
      ]),
      row([
        "Liepsna",
        imageBlock("/assets/hygieneh15/2/2.jpg", "Liepsna"),
        lines(
          "Degiosios dujos;",
          "Degieji aerozoliai;",
          "Degieji skysčiai;",
          "Degiosios kietosios medžiagos;",
          "Savaime reaguojančios medžiagos ir mišiniai;",
          "Piroforiniai skysčiai;",
          "Piroforinės kietosios medžiagos;",
          "Savaime kaistančios medžiagos ir mišiniai."
        ),
      ]),
      row([
        "Dujų balionas",
        imageBlock("/assets/hygieneh15/2/3.jpg", "Dujų balionas"),
        lines(
          "Slėgio veikiamos dujos;",
          "Suslėgtosios dujos;",
          "Suskystintosios dujos;",
          "Atšaldytos suskystintosios dujos;",
          "Ištirpintosios dujos;",
          "Medžiagos ir mišiniai, kontaktuodami su vandeniu, išskiriantys degiąsias dujas;",
          "Organiniai peroksidai."
        ),
      ]),
      row([
        "Korozija",
        imageBlock("/assets/hygieneh15/2/4.jpg", "Korozija"),
        lines("Ėsdina metalą;", "Odos ėsdinimas;", "Smarkus akių pažeidimas."),
      ]),
      row([
        "Kaukolė ir sukryžiuoti kaulai",
        imageBlock("/assets/hygieneh15/2/5.jpg", "Kaukolė ir sukryžiuoti kaulai"),
        "",
      ]),
      row([
        "Pavojai sveikatai",
        imageBlock("/assets/hygieneh15/2/6.jpg", "Pavojai sveikatai"),
        lines(
          "Kvėpavimo takų jautrinimas;",
          "Mutageninis poveikis lytinėms ląstelėms;",
          "Kancerogeniškumas;",
          "Toksinis poveikis reprodukcijai;",
          "Specifinis toksiškumas konkrečiam organui;",
          "Plaučių pakenkimo prarijus pavojus."
        ),
      ]),
      row([
        "Šauktukas",
        imageBlock("/assets/hygieneh15/2/7.jpg", "Šauktukas"),
        lines(
          "Ūmus toksiškumas (prarijus, per oda, įkvėpus);",
          "Odos dirginimas;",
          "Akių dirginimas;",
          "Odos jautrinimas;",
          "Specifinis toksiškumas konkrečiam organui (vienkartinis poveikis);",
          "Kvėpavimo takų dirginimas;",
          "Narkotinis poveikis."
        ),
      ]),
      row([
        "Aplinka",
        imageBlock("/assets/hygieneh15/2/8.jpg", "Aplinka"),
        "Pavojinga vandens aplinkai.",
      ]),
    ],
  }),
  genericEntry("drinkable-water-table", "Geriamojo vandens lentelė", {
    className: "table",
    head: [row(["Rodiklio pavadinimas", "Rodiklio ribinė vertė", "Matavimo vienetas"])],
    body: [
      row(["Žarninės lazdelės (Escherichia coli)", "0 / neaptikta", "skaičius / 100 ml"]),
      row(["Žarniniai enterokokai", "0 / neaptikta", "skaičius / 100 ml"]),
    ],
  }),
  genericEntry("packed-water-table", "Fasuoto vandens lentelė", {
    className: "table",
    head: [row(["Rodiklio pavadinimas", "Rodiklio ribinė vertė", "Matavimo vienetas"])],
    body: [
      row(["Žarninės lazdelės (Escherichia coli)", "0 / neaptikta", "skaičius / 250 ml"]),
      row(["Žarniniai enterokokai", "0 / neaptikta", "skaičius / 250 ml"]),
    ],
  }),
  genericEntry("water-system-table", "Vandentiekio sistemos lentelė", {
    className: "table",
    head: [row(["Rodiklio pavadinimas", "Rodiklio vertė", "Matavimo vienetas", "Pastaba"])],
    body: [
      row([
        "Legionella bakterijos",
        "< 1 000",
        "KSV/l",
        "Rodiklis taikomas vidaus vandentiekio sistemos rizikai vertinti. Infekcijų ar protrūkių atvejais rizikos mažinimo priemonės gali būti taikomos ir neviršijus šios vertės.",
      ]),
      row([
        "Švinas",
        "10 µg/l",
        "̶",
        "Rodiklis taikomas pastatų vidaus vandentiekio sistemų rizikai vertinti. Nuo 2036 m. sausio 12 d. bus taikoma vertė – 5 µg/l.",
      ]),
    ],
  }),
  genericEntry("exposition-table", "Ekspozicijos lentelė", {
    className: "table",
    head: [row(["Patogenas", "Užsikrėtimo rizika po adatos dūrio"])],
    body: [
      row(["HBV (jei nepasiskiepijęs)", "iki 30%"]),
      row(["HCV", "~1.8%"]),
      row(["ŽIV", "~0.3%"]),
    ],
  }),
  genericEntry("aap-table", "AAP lentelė", {
    className: "table",
    head: [row(["Veikla", "Rekomenduojamos AAP"])],
    body: [
      row([
        "Žmogaus palaikų paruošimas (plovimas, dezinfekcija, aprengimas)",
        "Dvigubos pirštinės, chalatas, veido kaukė ar respiratorius, apsauginiai akiniai / skydelis",
      ]),
      row([
        "Darbas su kūno skysčiais / užterštais paviršiais",
        "Neperšlampamas chalatas, pirštinės, veido apsauga",
      ]),
      row([
        "Šarvojimo salės priežiūra",
        "Pirštinės, jei kontaktuojama su paviršiais; higiena po kiekvieno kontakto",
      ]),
      row([
        "Atliekų tvarkymas (biologinės / aštrios atliekos)",
        "Storos pirštinės, jei reikia – apsauginiai batai",
      ]),
      row(["Kremavimas", "Atspari šilumai apranga, respiratorius, apsauginiai akiniai"]),
    ],
  }),
  genericEntry("adr-table", "ADR lentelė", {
    className: "table",
    head: [row(["Piktograma", "Klasė", "Pavojingumo kategorija"])],
    body: [
      row([
        imageBlock("/assets/hygieneh13/section2/1.gif", "1.1"),
        lines("1.1", "Sprogstamoji medžiaga ar gaminys, 1.1 poklasis"),
        "Masinio sprogimo pavojus",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/2.gif", "1.2"),
        lines("1.2", "Sprogstamoji medžiaga ar gaminys, 1.2 poklasis"),
        "Išsvaidymo pavojus",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/3.gif", "1.3"),
        lines("1.3", "Sprogstamoji medžiaga ar gaminys, 1.3 poklasis"),
        "Gaisro pavojus arba nedidelis išsvaidymo pavojus",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/4.gif", "1.4"),
        lines("1.4", "Sprogstamoji medžiaga ar gaminys, 1.4 poklasis"),
        "Nedidelis pavojus",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/5.gif", "1.5"),
        lines("1.5", "Sprogstamoji medžiaga ar gaminys, 1.5 poklasis"),
        "Labai mažo jautrumo medžiagos, keliančios masinio sprogimo pavojų",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/6.gif", "1.6"),
        lines("1.6", "Sprogstamoji medžiaga ar gaminys, 1.6 poklasis"),
        "Itin mažo jautrumo gaminiai, nekeliantys masinio sprogimo pavojaus",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/7.gif", "2.1"),
        lines("2.1", "Liepsniosios dujos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/8.jpg", "2.2"),
        lines("2.2", "Nedegios, nenuodingos dujos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/9.jpg", "2.3"),
        lines("2.3", "Nodingos dujos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/10.gif", "3"),
        lines("3", "Liepsnieji skysčiai"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/11.gif", "4.1"),
        lines(
          "4.1",
          "Degios kietosios medžiagos, savaime reaguojančios medžiagos ir kieti sumažinto jautrumo sprogmenys"
        ),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/12.jpg", "4.2"),
        lines("4.2", "Medžiagos, linkusios savaime užsidegti"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/13.gif", "4.3"),
        lines("4.3", "Medžiagos, kurios sąlytyje su vandeniu išskiria degias dujas"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/14.jpg", "5.1"),
        lines("5.1", "Oksiduojančios medžiagos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/15.jpg", "5.2"),
        lines("5.2", "Organiniai peroksidai"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/16.jpg", "6.1"),
        lines("6.1", "Nodingos dujos"),
        "",
      ]),
      row([imageBlock("/assets/hygieneh13/section2/17.jpg", "6.1"), lines("6.1", "Nuodai"), ""]),
      row([
        imageBlock("/assets/hygieneh13/section2/18.jpg", "6.2"),
        lines("6.2", "Infekcinės medžiagos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/19.jpg", "7"),
        lines("7", "Radioaktyvios medžiagos"),
        "Kategorija I",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/20.jpg", "7"),
        lines("7", "Radioaktyvios medžiagos"),
        "Kategorija II",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/21.jpg", "7"),
        lines("7", "Radioaktyvios medžiagos"),
        "Kategorija III",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/22.jpg", "7"),
        lines("7", "Radioaktyvios medžiagos"),
        "Kritiškumo saugos indekso etiketė",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/23.jpg", "8"),
        lines("8", "Korozinės medžiagos"),
        "",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/24.jpg", "9"),
        lines("9", "Įvairios pavojingos medžiagos ir dirbiniai"),
        "",
      ]),
    ],
  }),
  genericEntry("ghs-table", "GHS lentelė", {
    className: "table",
    head: [row(["Piktograma", "Pavadinimas", "Pavojaus klasė ir pavojaus kategorija"])],
    body: [
      row([
        imageBlock("/assets/hygieneh13/section2/25.gif", "Sprogstanti bomba"),
        "Sprogstanti bomba",
        lines(
          "2.1 skirsnis: Nestabilios sprogiosios medžiagos",
          "1.1, 1.2, 1.3, 1.4 poklasių sprogiosios medžiagos",
          "2.8 skirsnis: Savaime reaguojančios medžiagos ir mišiniai, A ir B tipai",
          "2.15 skirsnis: Organiniai peroksidai, A ir B tipai"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/26.gif", "Liepsna"),
        "Liepsna",
        lines(
          "2.2 skirsnis: Degiosios dujos, 1 pavojaus kategorija",
          "2.3 skirsnis: Degieji aerozoliai, 1 ir 2 pavojaus kategorijos",
          "2.6 skirsnis: Degieji skysčiai, 1, 2 ir 3 pavojaus kategorijos",
          "2.7 skirsnis: Degiosios kietosios medžiagos, 1 ir 2 pavojaus kategorijos",
          "2.8 skirsnis: Savaime reaguojančios medžiagos ir mišiniai, B, C, D, E, F tipai",
          "2.9 skirsnis: Piroforiniai skysčiai, 1 pavojaus kategorija",
          "2.10 skirsnis: Piroforinės kietosios medžiagos, 1 pavojaus kategorija",
          "2.11 skirsnis: Savaime kaistančios medžiagos ir mišiniai, 1, 2 pavojaus kategorijos",
          "2.12 skirsnis: Medžiagos ir mišiniai, kontaktuodami su vandeniu išskiriantys degiąsias dujas, 1, 2 ir 3 pavojaus kategorijos",
          "2.15 skirsnis: Organiniai peroksidai, B, C, D, E ir F tipai"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/27.gif", "Liepsnojantis lankas"),
        "Liepsnojantis lankas",
        lines(
          "2.4 skirsnis: Oksiduojančiosios dujos, 1 pavojaus kategorija",
          "2.13 skirsnis: Oksiduojantieji skysčiai, 1, 2 ir 3 pavojaus kategorijos",
          "2.14 skirsnis: Oksiduojančiosios kietosios medžiagos, 1, 2 ir 3 pavojaus kategorijos"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/28.gif", "Dujų balionas"),
        "Dujų balionas",
        lines(
          "2.5 skirsnis: Slėgio veikiamos dujos:",
          "Suslėgtosios dujos;",
          "Suskystintosios dujos;",
          "Atšaldytos suskystintosios dujos;",
          "Ištirpintosios dujos"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/29.gif", "Korozija"),
        "Korozija",
        lines(
          "2.16 skirsnis: Ėsdina metalą, 1 pavojaus kategorija",
          "3.2 skirsnis: Odos ėsdinimas, 1A, 1B ir 1C pavojaus kategorijos",
          "3.3 skirsnis: Smarkus akių pažeidimas, 1 pavojaus kategorija"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/30.gif", "Kaukolė ir sukryžiuoti kaulai"),
        "Kaukolė ir sukryžiuoti kaulai",
        "3.1 skirsnis: Ūmus toksiškumas (prarijus, per odą, įkvėpus), 1, 2 ir 3 pavojaus kategorijos",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/31.gif", "Šauktukas"),
        "Šauktukas",
        lines(
          "3.1 skirsnis: Ūmus toksiškumas (prarijus, per odą, įkvėpus), 4 pavojaus kategorija",
          "3.2 skirsnis: Odos dirginimas, 2 pavojaus kategorija",
          "3.3 skirsnis: Akių dirginimas, 2 pavojaus kategorija",
          "3.4 skirsnis: Odos jautrinimas, 1 pavojaus kategorija",
          "3.8 skirsnis: Specifinis toksiškumas konkrečiam organui (vienkartinis poveikis), 3 pavojaus kategorija",
          "Kvėpavimo takų dirginimas",
          "Narkotinis poveikis"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/32.gif", "Pavojai sveikatai"),
        "Pavojai sveikatai",
        lines(
          "3.4 skirsnis: Kvėpavimo takų jautrinimas, 1 pavojaus kategorija",
          "3.5 skirsnis: Mutageninis poveikis lytinėms ląstelėms, 1A, 1B ir 2 pavojaus kategorijos",
          "3.6 skirsnis: Kancerogeniškumas, 1A, 1B ir 2 pavojaus kategorijos",
          "3.7 skirsnis: Toksinis poveikis reprodukcijai, 1A, 1B ir 2 pavojaus kategorijos",
          "3.8 skirsnis: Specifinis toksiškumas konkrečiam organui (vienkartinis poveikis), 1, 2 pavojaus kategorijos",
          "3.9 skirsnis: Specifinis toksiškumas konkrečiam organui (kartotinis poveikis), 1, 2 pavojaus kategorijos",
          "3.10 skirsnis: Plaučių pakenkimo prarijus pavojus, 1 pavojaus kategorija"
        ),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/33.gif", "Aplinka"),
        "Aplinka",
        lines(
          "4.1 skirsnis: Pavojinga vandens aplinkai",
          "1 ūmaus pavojaus kategorija",
          "1, 2 lėtinio pavojaus kategorijos"
        ),
      ]),
    ],
  }),
  genericEntry("chemical-hazard-table", "Cheminių pavojų lentelė", {
    className: "table",
    head: [row(["Piktograma", "Pavojaus klasė ir pavojaus kategorija"])],
    body: [
      row([imageBlock("/assets/hygieneh13/section2/34.gif", "Oksiduojanti"), "O: Oksiduojanti"]),
      row([
        imageBlock("/assets/hygieneh13/section2/35.gif", "Toksiška"),
        lines("T: Toksiška", "T+: Labai toksiška"),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/36.gif", "Ardanti (ėsdinanti)"),
        "C: Ardanti (ėsdinanti)",
      ]),
      row([imageBlock("/assets/hygieneh13/section2/37.gif", "Sprogstamoji"), "E: Sprogstamoji"]),
      row([
        imageBlock("/assets/hygieneh13/section2/38.gif", "Aplinkai pavojinga"),
        "N: Aplinkai pavojinga",
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/39.gif", "Kenksminga / Dirginanti"),
        lines("Xn: Kenksminga", "Xi: Dirginanti"),
      ]),
      row([
        imageBlock("/assets/hygieneh13/section2/40.gif", "Labai degi / Ypač degi"),
        lines("F: Labai degi", "F+: Ypač degi"),
      ]),
    ],
  }),
  genericEntry("hold-table", "Laikymo lentelė", {
    className: "table",
    head: [row(["Atliekų tipas", "Temperatūra", "Maks. laikymo trukmė"])],
    body: [
      row(["Infekuotos atliekos (išskyrus aštrius daiktus)", "≥ +15 °C", "Iki 72 val."]),
      row(["Infekuotos atliekos (išskyrus aštrius daiktus)", "< +15 °C", "Iki 7 dienų"]),
      row(["Atliekos be specialių reikalavimų", "< +10 °C", "Iki 30 dienų"]),
      row(["Atliekos be specialių reikalavimų", "> +10 °C", "Iki 7 dienų"]),
      row(["Aštrūs daiktai (18 01 01, 18 01 03*)", "-", "Iki 30 dienų"]),
      row(["Anatomija", "< +10 °C", "Iki 7 dienų"]),
      row(["Užšaldytos atliekos (–18 °C ir mažiau)", "-", "Iki 6 mėn."]),
    ],
  }),
  genericEntry("daily-room-table", "Kasdienio valymo lentelė", {
    className: "table",
    head: [row(["Kasdieninė patalpų priežiūra"])],
    body: [
      row(["Visos patalpos kasdien turi būti vėdinamos ir valomos drėgnu būdu."]),
      row(["Visi liečiami paviršiai ir baldai turi būti valomi kasdien drėgnu būdu."]),
      row(["Patalpos gali būti vėdinamos skersvėjiniu būdu, kai patalpose nėra žmonių."]),
      row(["Vėdinamų patalpų temperatūra neturi nukristi žemiau 12 °C"]),
      row([
        "Tualetų valymo inventorius turi būti ženklinamas ryškia spalva, švarus ir sausas laikomas atskirai nuo kitų patalpų valymo inventoriaus.",
      ]),
      row([
        "Valytojos, valydamos tualetus, turi vilkėti (mūvėti) specialius drabužius. Jie turi būti paženklinti ir laikomi atskirai nuo kitų drabužių.",
      ]),
      row([
        "Valikliai turi būti naudojami pagal paskirtį. Naudojami biocidai teisės akto nustatyta tvarka turi būti leisti naudoti.",
      ]),
      row([
        "Asmenys, dirbantys su valymo, dezinfekcijos, dezinsekcijos ir deratizacijos priemonėmis, turi mokėti naudotis šiomis priemonėmis ir laikytis gamintojo ar tiekėjo saugos duomenų lapuose nurodytų sveikatos saugos reikalavimų.",
      ]),
      row([
        "Draudžiama atlikti baigiamąją dezinfekciją, dezinsekciją, deratizaciją žmonėms (vaikams) esant patalpose.",
      ]),
      row(["Patalpose turi nebūti graužikų ir kitų buitinių kenkėjų."]),
      row(["Dezinsekciją ir deratizaciją atlieka šiai veiklai licencijuoti juridiniai asmenys"]),
    ],
  }),
  genericEntry("infection-table", "Infekcijų lentelė", {
    className: "table",
    head: [row(["Infekcijų tipas", "Pavyzdžiai"])],
    body: [
      row([
        "Žarnyno infekcijos",
        "Salmoneliozė, rotovirusinė infekcija, kampilobakteriozė, stafilokokinė toksikozė",
      ]),
      row([
        "Kvėpavimo takų infekcijos",
        "Meningokokinė infekcija, TBC, gripas, legioneliozė, vėjaraupiai, tymai, covid-19",
      ]),
      row(["Kraujo infekcijos", "Virusiniai hepatitai B ir C, ŽIV infekcija"]),
      row([
        "Kūno dangų infekcijos",
        "LPL, odos ir nagų grybeliai, virusinės infekcijos (herpės inf.), pedikuliozė (utėlėtumas) ir niežai",
      ]),
    ],
  }),
  genericEntry("hep-table", "Hepatito lentelė", {
    className: "table",
    head: [row(["Kriterijus", "Hepatitas B (HBV)", "Hepatitas C (HCV)"])],
    body: [
      row([
        "Perdavimo būdai",
        "Kraujas, kūno skysčiai, lytinis kontaktas",
        "Kraujas, retai – lytinis kontaktas",
      ]),
      row(["Vakcina", "Yra vakcina nuo hepatito B", "Nėra vakcinos nuo hepatito C"]),
      row([
        "Gydymas",
        "Antivirusiniai vaistai, kai kurie atvejai praeina savaime",
        "Antivirusiniai vaistai (DAA), 95% efektyvumas",
      ]),
      row([
        "Simptomai",
        "Ūminiai simptomai dažni, lėtinis dažnai be simptomų",
        "Dažnai besimptomis, ūminiai simptomai rečiau",
      ]),
      row([
        "Lėtinė forma",
        "Gali tapti lėtine, ilgalaikės komplikacijos",
        "Dažniau pereina į lėtinę formą, gali sukelti cirozę ir kepenų vėžį",
      ]),
    ],
  }),
  genericEntry("transport-table", "Transporto lentelė", {
    className: "table",
    head: [row(["Rodiklis", "Leidžiama vertė"])],
    body: [
      row(["Oro temperatūra žiemą", "+16 °C – +24 °C"]),
      row(["Oro temperatūra vasarą", "+18 °C – +28 °C"]),
      row(["Oro drėgmė", "40–70 %"]),
      row(["Oro judėjimo greitis", "iki 0,2 m/s (šaltuoju metu), iki 0,5 m/s (šiltesniu)"]),
      row(["CO₂ koncentracija", "ne daugiau kaip 1000 ppm"]),
    ],
  }),
  genericEntry("registry-table", "Registracijos lentelė", {
    className: "table",
    head: [row(["Eilės numeris", "Patalpos pavadinimas", "Oro temperatūra, °C"])],
    body: [
      row([
        "1",
        "Patalpos, kuriose įrengtos vaikų individualios veiklos vietos, stacionarios kompiuterizuotos vietos, pamokų ruošos kambarys, bendrasis kambarys, miegamasis",
        "18–22",
      ]),
      row(["2", "Renginių ir kūno kultūros salė", "17–20"]),
      row(["3", "Vestibiulis, drabužinė", "18–20"]),
      row(["4", "Tualetas", "18–22"]),
      row(["5", "Vonios (dušo) patalpa", "20–24"]),
      row(["6", "Laiptinės, koridoriai", "18–20"]),
    ],
  }),
];

const legacyDefinitions: TableRegistryEntry[] = [];

export const tableRegistryEntries: TableRegistryEntry[] = [
  ...genericDefinitions,
  ...legacyDefinitions,
];

export const tableRegistry = tableRegistryEntries.reduce<Record<string, TableRegistryEntry>>(
  (accumulator, entry) => {
    accumulator[entry.type] = entry;
    return accumulator;
  },
  {}
);

export const isRegisteredTableType = (type: string) => Boolean(tableRegistry[type]);

export const renderTableByType = (type: string) => tableRegistry[type]?.render() || null;
